/**
 * La evolución del peso, una sola gráfica para el cliente y para el entrenador.
 * Punto 4.13 de la revisión del 09-08.
 *
 * Lo que reportó Jesús: «puntos naranjas sobre una cuadrícula. Sin ejes, sin fechas, sin
 * valores, sin línea de unión, sin tooltip».
 *
 * La causa de lo de los ejes es tonta y por eso costaba verla: estaban pintados con
 * `fill: '#ffffff66'` a pelo. En el panel del entrenador el fondo es negro y se leen; en el
 * lado del cliente la tarjeta es BLANCA en tema claro, así que era **texto blanco sobre
 * blanco**. La cuadrícula (`#222`, gris oscuro) y los puntos naranjas sí se veían. De ahí la
 * descripción exacta: la gráfica estaba entera, solo que la mitad era invisible.
 *
 * Aquí los colores salen de las variables del tema, así que se leen en los dos fondos y
 * siguen leyéndose si alguien cambia la paleta.
 *
 * Lo demás que faltaba:
 *
 *   - LA LÍNEA SE CORTABA. Un hueco sin peso partía la línea en trozos y con datos salteados
 *     no quedaba más que la nube de puntos. `connectNulls` la mantiene entera.
 *   - LAS FECHAS SE PISABAN. La etiqueta era «9 ago» sin año, y con dietas desde 2022 hay
 *     cuatro «9 ago» distintos que el eje trata como la misma categoría. Ahora el eje es
 *     numérico por tiempo y las etiquetas llevan año cuando el rango pasa de un año.
 *   - NO SE VEÍA LO QUE IMPORTA. Debajo del título van el peso de partida, el de ahora y el
 *     cambio, que es lo que se mira antes que la curva.
 *
 * EL PESO EN DOS BLOQUES, Y FUERA LA GRÁFICA LARGA (doc de Jesús del 2-09, «Los dos bloques
 * del peso»). Hasta aquí la curva llevaba TODOS los pesajes de la historia, y «18 pesajes de
 * tres años y medio en el ancho de un móvil son una línea que no se lee». Ahora:
 *
 *   - ARRIBA, ESTE CICLO: la curva solo con los pesajes desde `cycle_start`, y tres líneas
 *     (dónde empezó el ciclo, ahora, y los kilos de estas N semanas). Contesta «¿cómo voy?».
 *   - DEBAJO, DESDE QUE ENTRÓ: sin gráfica, en tres líneas. Lo que pesaba al entrar, lo que
 *     pesa hoy y desde cuándo está. «La historia entera sigue estando, pero deja de ser el
 *     primer número que ve.» Solo se pinta si hay pesajes anteriores al ciclo: si toda su
 *     historia cabe en el ciclo no hay nada que contar dos veces.
 *
 * Y ENCIMA DE TODO, SU OBJETIVO, DE SOLO LECTURA (doc de Jesús del 2-09, «Los ciclos, los
 * bloques y los objetivos»; decisión de Francisco del 4-09). Los objetivos los pone el
 * ENTRENADOR, no el cliente, y aquí se le enseñan tal cual: el del ciclo, el actual si lo
 * matiza, el foco si lo hay y por dónde va («Bloque 3 · semana 12 de 12»). Hasta aquí era
 * una línea («Tu objetivo: ganar masa») sacada del `goal` del cuestionario.
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { kg } from '../lib/pesoValido';
import { nombreDelObjetivo, normalizarObjetivo, objetivoVisible } from '../lib/objetivos';

const NARANJA = '#FF671F';
// Del tema, no a pelo: es lo que hacía que en el lado del cliente no se leyeran.
const TENUE = 'hsl(var(--muted-foreground))';
const LINEA_TENUE = 'hsl(var(--border))';

// EL CAMBIO SE LEE CONTRA SU OBJETIVO (doc 23-08, bloque 10). «Cambio +7 kg» en naranja
// de alarma cuando el cliente está en volumen es contarle su progreso al revés: para él
// +7 es la buena noticia. El color solo dice algo si sabe hacia dónde va el cliente:
// verde si el cambio va hacia su objetivo, rojo si va en contra, y neutro si mantiene
// (o si su objetivo no marca dirección, como recomposición).
//
// +1 = el peso debe subir; -1 = debe bajar; 0 = sin dirección (mantener, recomposición
// o sin objetivo en el perfil). Las claves son las de la lista cerrada de objetivos
// (`lib/objetivos`, doc de Jesús del 2-09); el `goal` viejo del cuestionario (volumen /
// definicion) sigue entrando porque `normalizarObjetivo` lo traduce.
const DIRECCION = {
    ganar_volumen: 1,
    perder_grasa: -1, maxima_definicion: -1, tonificacion: -1,
    recomposicion: 0, mantenimiento: 0,
};
const _direccion = (objetivo) => DIRECCION[normalizarObjetivo(objetivo) || ''] || 0;
const _colorDelCambio = (delta, objetivo) => {
    const dir = _direccion(objetivo);
    if (!delta || !dir) return 'text-foreground';
    return delta * dir > 0 ? 'text-emerald-500' : 'text-red-500';
};

const _fecha = (ts, conAnio) => new Date(ts).toLocaleDateString('es-ES', {
    day: 'numeric', month: 'short', ...(conAnio ? { year: '2-digit' } : {}),
});
// «enero de 2023»: desde cuándo está con nosotros. A esa distancia el día no dice nada.
const _mesYAnio = (ts) => new Date(ts).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
// «+7 kg», «−5,6 kg», «0 kg»: el signo delante y la coma decimal, la misma que escribe el
// campo del peso que va encima en la tarjeta (`kg` de lib/pesoValido).
const _conSigno = (delta) => `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${kg(Math.abs(delta))} kg`;

const Globo = ({ active, payload, objetivo }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div className="rounded-xl border border-border bg-card px-3 py-2 shadow-lg">
            <p className="text-xs text-muted-foreground">{_fecha(p.ts, true)}</p>
            <p className="text-lg font-bold text-foreground leading-tight">
                {kg(p.peso)} <span className="text-xs font-normal text-muted-foreground">kg</span>
            </p>
            {p.delta != null && p.delta !== 0 && (
                // El mismo criterio que el resumen: bajar solo es verde si su objetivo
                // es bajar. Antes bajar era siempre verde y subir siempre naranja.
                <p className={`text-xs font-bold ${_colorDelCambio(p.delta, objetivo)}`}>
                    {_conSigno(p.delta)} desde el anterior
                </p>
            )}
        </div>
    );
};

// La curva de una serie: la de este ciclo, o la entera si no hay ciclo. El eje es de
// TIEMPO y no de categorías, y las etiquetas llevan año solo si el recorrido pasa del año.
const Curva = ({ serie, alto, objetivo }) => {
    // EL PRIMERO CON PESO, no el primero de la lista. Desde que la curva va por semanas
    // (Jesús, 8-09) la serie TRAE HUECOS a propósito -- «dibujarla con lo que haya... y el
    // hueco es un recordatorio» --, y con una semana vacía delante esto valía null y la
    // línea de referencia se iba al suelo.
    const primero = (serie.find(x => x.peso != null) || {}).peso ?? null;
    const conAnio = (serie[serie.length - 1].ts - serie[0].ts) > 330 * 24 * 3600 * 1000;
    return (
        <div className={alto}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={serie} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={LINEA_TENUE} />
                    {/* Eje de TIEMPO, no de categorías: con dietas desde 2022 había cuatro
                        «9 ago» distintos y el eje los juntaba en uno. */}
                    <XAxis
                        dataKey="ts" type="number" scale="time"
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={(t) => _fecha(t, conAnio)}
                        tick={{ fill: TENUE, fontSize: 11 }}
                        axisLine={false} tickLine={false} minTickGap={32}
                    />
                    {/* El eje con coma, como el resto de la pantalla (punto 71): sin
                        formateador Recharts escribía «78.5 kg» al lado de un «78,6 kg».
                        El « kg» se lo sigue poniendo `unit` (Recharts pega la unidad
                        DESPUÉS del formateador). Y el eje se ensancha: con 44 px no cabía
                        «79,5 kg» y Recharts lo partía en dos renglones («79,5» encima de
                        «kg»), que es como llevaba desde siempre. */}
                    <YAxis
                        domain={['auto', 'auto']} width={62} unit=" kg"
                        tickFormatter={(v) => kg(v)}
                        tick={{ fill: TENUE, fontSize: 11 }}
                        axisLine={false} tickLine={false}
                    />
                    <Tooltip content={<Globo objetivo={objetivo} />} cursor={{ stroke: LINEA_TENUE }} />
                    {/* De dónde salió: la referencia contra la que se lee todo lo demás. */}
                    <ReferenceLine y={primero} stroke={TENUE} strokeDasharray="4 4" />
                    <Line
                        type="monotone" dataKey="peso" stroke={NARANJA} strokeWidth={2}
                        connectNulls
                        dot={{ fill: NARANJA, r: 2.5 }}
                        activeDot={{ r: 5, fill: NARANJA, stroke: 'hsl(var(--card))', strokeWidth: 2 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

// El rótulo de cada bloque: pequeño en mayúsculas a la izquierda y, a la derecha, el
// cambio con signo. Es lo que Jesús dibujó en la maqueta del 2-09.
const Rotulo = ({ children, derecha = null, derechaClase = 'text-foreground', testid }) => (
    <div className="flex items-baseline justify-between gap-3">
        <p className="caption">{children}</p>
        {derecha != null && (
            <span data-testid={testid} className={`text-sm font-bold tabular-nums ${derechaClase}`}>
                {derecha}
            </span>
        )}
    </div>
);

// Una línea del resumen: la etiqueta a la izquierda y el valor en negrita a la derecha.
// El valor no se parte nunca («junio de 2026» salía en dos líneas en un móvil de 390);
// si falta sitio, la que envuelve es la etiqueta.
const Fila = ({ etiqueta, children }) => (
    <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="text-muted-foreground">{etiqueta}</span>
        <span className="font-bold text-foreground tabular-nums text-right whitespace-nowrap shrink-0">{children}</span>
    </div>
);

/**
 * LA TARJETA DEL OBJETIVO, tal como la dibujó Jesús en la maqueta del 2-09:
 *
 *     OBJETIVO DEL CICLO            VAS POR
 *     Perder grasa                  Bloque 3
 *     OBJETIVO ACTUAL               semana 12 de 12
 *     Recomposición
 *
 * Todo de SOLO LECTURA: los objetivos los pone el entrenador (Jesús: «es un cambio de
 * fondo, no de copy»; Francisco, 4-09). El actual va en naranja y SOLO si es distinto del
 * del ciclo: el mismo nombre dos veces no dice nada. Debajo, en gris, solo el foco
 * («tonificación con foco glúteo» son dos campos). Y «Vas por» solo si viene
 * `ciclo_actual`: sin él no se inventa un bloque.
 *
 * AQUÍ NO VA LA DEFINICIÓN DEL OBJETIVO (ficha 13 del doc del 5-09). Hasta hoy, con
 * «máxima definición» se le escribía debajo «Bajar del 14 % de grasa.», que salió del
 * artefacto del 2-09. Jesús: «el 14 o el 15 es mi criterio de trabajo, no el objetivo del
 * cliente», y además el % de grasa es opcional en la app y se lo mide él, así que el
 * cliente no tiene forma de saber si está en el 14. En la pantalla del cliente queda el
 * nombre y ya. La definición sigue viva en `lib/objetivos.js` para el panel del
 * entrenador (`panel/ObjetivoDelCliente`), que es de quien es el criterio.
 *
 * Sin ciclo abierto (ni `ciclo_actual` ni fecha de arranque) el rótulo es «Tu objetivo»,
 * por lo mismo que abajo se dice «Tu curva» y no «Este ciclo».
 */
const TarjetaDelObjetivo = ({ objetivoActual, foco, ciclo, conCiclo }) => {
    const delCiclo = normalizarObjetivo(ciclo?.objetivo) || objetivoActual;
    if (!delCiclo) return null;
    const actualDistinto = objetivoActual && objetivoActual !== delCiclo ? objetivoActual : null;
    const vasPor = ciclo != null && (ciclo.bloque != null || ciclo.semana != null);
    return (
        <div className="flex items-start justify-between gap-4 pb-3 border-b border-border"
             data-testid="objetivo-del-peso">
            <div className="space-y-1.5 min-w-0">
                <div>
                    <p className="caption">{conCiclo ? 'Objetivo del ciclo' : 'Tu objetivo'}</p>
                    <p className="text-base font-bold text-foreground leading-tight" data-testid="objetivo-del-ciclo">
                        {nombreDelObjetivo(delCiclo)}
                    </p>
                </div>
                {actualDistinto && (
                    <div>
                        <p className="caption">Objetivo actual</p>
                        <p className="text-base font-bold text-brand leading-tight" data-testid="objetivo-actual">
                            {nombreDelObjetivo(actualDistinto)}
                        </p>
                    </div>
                )}
                {foco && (
                    <p className="text-xs text-muted-foreground" data-testid="objetivo-foco">foco: {foco}</p>
                )}
            </div>
            {vasPor && (
                <div className="text-right shrink-0" data-testid="vas-por">
                    <p className="caption">Vas por</p>
                    {ciclo.bloque != null && (
                        <p className="text-base font-bold text-foreground leading-tight tabular-nums">
                            Bloque {ciclo.bloque}
                        </p>
                    )}
                    {ciclo.semana != null && (
                        <p className="text-xs text-muted-foreground tabular-nums">
                            semana {ciclo.semana}{ciclo.semanas ? ` de ${ciclo.semanas}` : ''}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * `puntos`: [{fecha|date, peso|value}]. Se acepta cualquiera de los dos nombres porque las
 * dos pantallas los traían distintos, y unificarlos aquí es más barato que tocar las dos.
 * `desdeElCiclo`: el `cycle_start` del perfil. `semanasDelCiclo`: su `cycle_total_weeks`,
 * para decir «En estas 12 semanas»; sin él se dice «En este ciclo».
 *
 * EL OBJETIVO ENTRA DE DOS FORMAS (fase 2 del doc del 2-09, 4-09):
 *   - `perfil`: la ficha entera del cliente, con `objetivo_actual`, `foco` y `ciclo_actual`
 *     ({numero, inicio, semanas, semana, bloque, objetivo}). Es la que usa Evolución.
 *   - `objetivo`: la prop de antes, una clave sola (el `goal` viejo o una de la lista
 *     nueva). Sigue valiendo para que el panel del entrenador no tenga que cambiar; con
 *     ella se pinta la tarjeta sin «Vas por».
 * Con las dos, manda la ficha. Si la ficha trae `ciclo_actual` y no se pasan
 * `desdeElCiclo` / `semanasDelCiclo`, salen de ahí (`inicio` y `semanas`).
 *
 * `conComparador`: pinta «Comparar con cualquier punto ›» al pie de «Desde que entraste»
 * (fase 3 del doc del 2-09, 4-09). Solo lo pasa la Evolución del cliente: en el panel del
 * entrenador la misma gráfica no lleva el enlace, porque va a Seguimiento del que mira.
 */
const GraficaDePeso = ({ puntos, alto = 'h-56', conResumen = true, objetivo = null, perfil = null,
                         desdeElCiclo = null, semanasDelCiclo = null, conComparador = false,
                         porSemanas = null }) => {
    const navigate = useNavigate();
    const ciclo = perfil?.ciclo_actual || null;
    // El objetivo que manda para el color del cambio: el actual de la ficha (que matiza al
    // del ciclo), y si no hay ficha, la clave suelta. Sin nada, sin dirección.
    const objetivoActual = perfil ? objetivoVisible(perfil) : normalizarObjetivo(objetivo);
    const foco = perfil?.foco || null;
    const arranque = desdeElCiclo ?? ciclo?.inicio ?? null;
    const semanas = semanasDelCiclo ?? ciclo?.semanas ?? null;
    // UN PUNTO POR SEMANA CUANDO EL SERVIDOR LO MANDA (Jesús, 8-09, decisión 1).
    //
    //     «La curva tiene tres puntos en doce semanas, y no son las fechas en que te
    //      pesaste: son las fechas de tus ajustes. Por eso de 72,7 a 63 parecen dos
    //      semanas. Con el peso semanal ya calculado, la curva puede tener un punto por
    //      semana: doce en un ciclo. Con tres no se ve una tendencia; con doce sí.»
    //
    // Las semanas SIN peso vienen igual, con `peso: null`, que es su decisión 3:
    // «dibujarla con lo que haya. Sale con huecos y se va llenando sola conforme cojan el
    // hábito. Y el hueco es un recordatorio».
    //
    // Sin `porSemanas` la gráfica se queda como estaba, un punto por pesaje: así las otras
    // pantallas que la usan (el panel del entrenador, Mis macros) no cambian hasta que se
    // decida llevarlas también.
    const semanales = useMemo(() => {
        if (!porSemanas?.length) return null;
        const filas = porSemanas
            .map(x => ({ ts: new Date(`${x.lunes}T12:00:00`).getTime(), peso: x.peso ?? null }))
            .filter(x => Number.isFinite(x.ts));
        if (!filas.some(x => x.peso != null)) return null;   // sin ningún peso no hay curva
        return filas.map((x, i) => {
            const previo = [...filas.slice(0, i)].reverse().find(y => y.peso != null);
            return {
                ...x,
                delta: (x.peso != null && previo) ? Math.round((x.peso - previo.peso) * 10) / 10 : null,
            };
        });
    }, [porSemanas]);

    // LOS SALTOS IMPOSIBLES NO SE DIBUJAN (Francisco, 11-09: «los pesos del histórico son
    // basura: 118, 94, 75, 50… saltando de 50 a 118 de una semana para otra, cincuenta
    // veces, y la app los pinta sin un aviso»).
    //
    // El servidor ya dice de cuál se fía (`fiable`, tope de 3 kg por semana en
    // `core/series_cliente`). Aquí solo se hace caso: el pesaje que no se sostiene se cae de
    // la línea, porque un diente de sierra no deja ver la tendencia, que es para lo que está
    // la gráfica. El dato NO se borra en ningún sitio, y se dice cuántos se quedaron fuera.
    //
    // Si la serie entera es dudosa el servidor no marca ninguno, así que aquí se pinta
    // completa: ahí no hay un dato malo que quitar.
    const fueraDeLaCurva = useMemo(
        () => (puntos || []).filter(p => p.fiable === false).length, [puntos]);

    const datosPorPesaje = useMemo(() => {
        const limpio = (puntos || [])
            .filter(p => p.fiable !== false)
            .map(p => {
                const cuando = p.fecha ?? p.date ?? p.ts;
                const kilos = Number(p.peso ?? p.value ?? p.weight);
                const ts = typeof cuando === 'number' ? cuando : new Date(cuando).getTime();
                return Number.isFinite(ts) && Number.isFinite(kilos) && kilos > 0 ? { ts, peso: kilos } : null;
            })
            .filter(Boolean)
            .sort((a, b) => a.ts - b.ts);
        // Un peso por día: dos pesajes el mismo día es una corrección, manda el último.
        // Es la misma regla que las series del backend, y sin ella la curva hace picos que
        // no existieron.
        const porDia = new Map();
        limpio.forEach(p => porDia.set(new Date(p.ts).toLocaleDateString('en-CA'), p)); // dia local, no UTC (bloque F)
        const unicos = [...porDia.values()].sort((a, b) => a.ts - b.ts);
        return unicos.map((p, i) => ({
            ...p,
            delta: i > 0 ? Math.round((p.peso - unicos[i - 1].peso) * 10) / 10 : null,
        }));
    }, [puntos]);

    // La semanal manda cuando existe: es la curva del 8-09.
    const datos = semanales || datosPorPesaje;
    if (!datos.length) return null;

    // ESTE CICLO ARRIBA; SU HISTORIA ENTERA, DEBAJO Y SIN CURVA (doc de Jesús del 2-09).
    //
    // «Empezaste en 89 · ahora 96 · cambio +7 kg» se calculaba con el primer pesaje de
    // TODA su historia: a un cliente migrado de Calma le juntaba enero de 2023 con hoy,
    // tres años y tres ciclos en un solo número, y encima debajo de «tu objetivo: perder
    // grasa». Lo que le interesa es cómo va en el ciclo que está haciendo, y la curva larga
    // ni siquiera se lee en un móvil.
    //
    // Sin fecha de ciclo se sigue leyendo la historia entera como hasta ahora, con el
    // rótulo «Tu curva»: es mejor que quedarse sin resumen.
    const arranqueCiclo = arranque ? new Date(arranque).getTime() : null;
    const hayCiclo = arranqueCiclo != null && Number.isFinite(arranqueCiclo);
    const serie = hayCiclo ? datos.filter(p => p.ts >= arranqueCiclo) : datos;
    // Pesajes de ANTES del ciclo: son los que justifican el segundo bloque.
    const anterioresAlCiclo = datos.length - serie.length;

    // SOLO LO QUE TIENE PESO, y esto es el «−9,7 kg en estas 12 semanas» que él denuncia
    // (bloque A del 8-09: «se arregla de paso; sale de restar dos pesadas sueltas»).
    //
    // Desde que la curva va por SEMANAS la serie lleva huecos a propósito, y todas las
    // cuentas de aquí abajo restaban el último menos el primero A SECAS. Con la primera
    // semana vacía eso era «63,4 − null», o sea **+63,4 kg de cambio**, que no es un cambio:
    // es el peso entero. Y «Empezaste el ciclo en» ponía «null kg».
    const conPeso = serie.filter(x => x.peso != null);
    const conTodos = datos.filter(x => x.peso != null);

    if (!conResumen) {
        return conPeso.length >= 2
            ? <div data-testid="grafica-de-peso"><Curva serie={serie} alto={alto} objetivo={objetivoActual} /></div>
            : null;
    }

    const ultimoDeTodos = conTodos.length ? conTodos[conTodos.length - 1].peso : null;
    const cambioTotal = conTodos.length >= 2
        ? Math.round((ultimoDeTodos - conTodos[0].peso) * 10) / 10 : null;

    // Las tres líneas del primer bloque, o el texto de «te falta uno» si no hay curva.
    const conCurva = conPeso.length >= 2;
    const cambio = conCurva
        ? Math.round((conPeso[conPeso.length - 1].peso - conPeso[0].peso) * 10) / 10 : null;
    const colorCambio = cambio === 0 ? 'text-foreground' : _colorDelCambio(cambio, objetivoActual);
    const rotuloCiclo = hayCiclo ? 'Este ciclo' : 'Tu curva';
    // «En estas 12 semanas» es la duración del ciclo del perfil (`cycle_total_weeks`); los
    // planes mensuales sin tope no la tienen, y ahí se dice «En este ciclo».
    const etiquetaCambio = !hayCiclo ? 'Desde entonces'
        : semanas ? `En estas ${semanas} semanas` : 'En este ciclo';

    return (
        <div data-testid="grafica-de-peso" className="space-y-4">
            {/* Encima de todo, hacia dónde va: sin esto un +7 no se puede leer, porque no
                es lo mismo en volumen que en definición (doc 23-08, bloque 10). Desde el
                4-09 es la tarjeta de la maqueta de Jesús, de solo lectura. */}
            <TarjetaDelObjetivo objetivoActual={objetivoActual} foco={foco} ciclo={ciclo}
                                conCiclo={ciclo != null || hayCiclo} />

            {/* BLOQUE 1: este ciclo (o su curva entera, si no hay fecha de ciclo). */}
            <div className="space-y-2">
                <Rotulo derecha={conCurva ? _conSigno(cambio) : null} derechaClase={colorCambio}
                        testid="cambio-de-peso">
                    {rotuloCiclo}
                    {conCurva && (
                        /* «N SEMANAS CON PESO», no «N pesajes» (Jesús, 8-09, decisión 2):
                           «con la regla nueva un cliente se pesa dos veces por semana, así
                           que en un ciclo entero diría 24 pesajes y ese número no dice nada.
                           Lo que importa es de cuántas semanas tienes dato. Se lee al lado
                           de las 12 del ciclo y se entiende solo». */
                        <span className="normal-case tracking-normal font-normal">
                            {' '}· {semanales
                                ? `${conPeso.length} ${conPeso.length === 1 ? 'semana' : 'semanas'} con peso`
                                : `${conPeso.length} pesajes`}
                        </span>
                    )}
                </Rotulo>
                {conCurva ? (
                    <>
                        <Curva serie={serie} alto={alto} objetivo={objetivoActual} />
                        {/* Y SE DICE, que esconder un dato sin avisar es peor que pintarlo:
                            el que se pesó ese día y no ve su punto tiene derecho a saber por
                            qué. No se riñe a nadie: la báscula falla, y un 118 entre setentas
                            casi siempre es eso. */}
                        {fueraDeLaCurva > 0 && (
                            <p className="text-xs text-muted-foreground mt-1"
                                data-testid="pesajes-fuera-de-la-curva">
                                {fueraDeLaCurva === 1
                                    ? 'Hay un pesaje que se sale de lo posible y no lo pintamos.'
                                    : `Hay ${fueraDeLaCurva} pesajes que se salen de lo posible y no los pintamos.`}
                                {' '}Siguen guardados.
                            </p>
                        )}
                        <div className="space-y-1.5 pt-1">
                            <Fila etiqueta={hayCiclo ? 'Empezaste el ciclo en' : 'Empezaste en'}>
                                {kg(conPeso[0].peso)} kg
                            </Fila>
                            <Fila etiqueta="Ahora">{kg(conPeso[conPeso.length - 1].peso)} kg</Fila>
                            <Fila etiqueta={etiquetaCambio}>
                                <span className={colorCambio}>{_conSigno(cambio)}</span>
                            </Fila>
                        </div>
                    </>
                ) : (
                    // CON UN SOLO PESAJE NO SE DIBUJA UNA CURVA.
                    // Salía una gráfica con un punto y «empezaste en 75,5 · ahora 75,5 · cambio
                    // 0 kg · 1 pesaje»: una línea recta que no dice nada y que parece un error.
                    // Es la pantalla del premio, la que enseña que el trabajo sirve, y con un
                    // punto enseña lo contrario. Se dice lo que falta para tenerla, que además
                    // es una petición concreta (Jesús, 11-08). Con ciclo pasa lo mismo dentro
                    // del ciclo: su historia de antes está en el bloque de abajo.
                    <div className="surface p-4">
                        {conPeso.length === 1 ? (
                            <p className="text-sm text-foreground">
                                {anterioresAlCiclo > 0 ? 'Tu primer peso del ciclo' : 'Tu primer peso'}:{' '}
                                <span className="font-bold">{kg(conPeso[0].peso)} kg</span>
                            </p>
                        ) : (
                            <p className="text-sm text-foreground">Todavía no te has pesado en este ciclo.</p>
                        )}
                        <p className="text-sm text-muted-foreground mt-1">
                            Con dos pesajes te dibujamos tu evolución.{serie.length === 1 ? ' Te falta uno.' : ''}
                        </p>
                    </div>
                )}
            </div>

            {/* BLOQUE 2: desde que entró, en texto y sin gráfica. Solo si hay pesajes de
                antes del ciclo. Sin color en el cambio: es contexto, no es cómo va este
                ciclo, y el verde o el rojo se reservan para lo que se está juzgando.
                Y CON EL NÚMERO DE CICLOS, como pide («3 ciclos desde enero de 2023»). Se
                dejó fuera el 4-09 porque el dato no existía; con el cuaderno de ciclos ya
                existe: `ciclo_actual.numero` es la vuelta en la que va, y en producción va
                del 1 al 14. Es el mismo número que la app ya le enseña en otras pantallas,
                con su vieja salvedad: al que renueva por Stripe se le reescribe el ancla y
                sale 1 aunque lleve años. Sin número, la línea de siempre. */}
            {hayCiclo && anterioresAlCiclo > 0 && conTodos.length >= 2 && (
                <div className="pt-3 border-t border-border space-y-1.5" data-testid="peso-historia-entera">
                    <Rotulo derecha={_conSigno(cambioTotal)}>Desde que entraste</Rotulo>
                    <Fila etiqueta="Cuando entraste pesabas">{kg(conTodos[0].peso)} kg</Fila>
                    <Fila etiqueta="Ahora">{kg(ultimoDeTodos)} kg</Fila>
                    {ciclo?.numero ? (
                        <Fila etiqueta="Llevas">
                            {ciclo.numero} {ciclo.numero === 1 ? 'ciclo' : 'ciclos'} desde {_mesYAnio(conTodos[0].ts)}
                        </Fila>
                    ) : (
                        <Fila etiqueta="Llevas con nosotros desde">{_mesYAnio(conTodos[0].ts)}</Fila>
                    )}
                    {/* «¿Cómo estaba yo en el ciclo 1?» se contesta en el comparador (doc
                        del 2-09; fase 3, 4-09): este bloque acaba en el enlace que lo abre,
                        con el punto de hoy ya elegido. Solo en la Evolución del cliente. */}
                    {conComparador && (
                        <button type="button" data-testid="comparar-con-cualquier-punto"
                            onClick={() => navigate('/dashboard/reports?abrir=comparar')}
                            className="text-sm font-bold text-brand hover:underline pt-1">
                            Comparar con cualquier punto ›
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default GraficaDePeso;
